// PathSelectionFlow.jsx — Renders the right per-path selection UI for the
// chosen path. Each path resolves to its own internal component, all of which
// emit `onSelectionsChange(selections)` where selections is up-to-3 objects
// of shape { item_type, item_id, item_name_verbatim, kind }.
//
// Used inside SignUp.jsx after the user picks a path. Designed so the SAME
// component is also re-usable on the dashboard ("Unlock another path" flow).

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Check, Sparkles, AlertTriangle } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  fetchJobRolesData, findJobRoles,
  fetchSpecBranches, fetchSpecData,
  fetchAIDepartments,
  fetchBankingBlueFamilies, fetchBankingBlueData,
  fetchEntrepreneurshipData,
  fetchClarityQuiz,
} from '../lib/pathsApi';

const DEFAULT_MAX_SELECTIONS = 5;

const SelectionBar = ({ selections, onRemove, max }) => (
  <div className="sticky top-0 z-10 -mx-1 mb-4 px-1 py-2 bg-white/95 backdrop-blur border-b border-gray-100">
    <div className="text-xs font-semibold text-gray-500 mb-1">Your picks ({selections.length}/{max})</div>
    {selections.length === 0 ? (
      <p className="text-xs text-gray-400">Tap a card below — first pick becomes your primary.</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {selections.map((s) => (
          <span key={s.item_id} className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.kind === 'primary' ? 'bg-teal-100 text-teal-800' : 'bg-blue-50 text-blue-700'}`}>
            {s.kind === 'primary' && <Sparkles size={10} />} {s.item_name_verbatim}
            <button type="button" onClick={() => onRemove(s)} className="ml-1 hover:text-rose-600">×</button>
          </span>
        ))}
      </div>
    )}
  </div>
);

const Card = ({ children, onClick, selected, kind, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left w-full border-2 rounded-xl p-4 transition-all ${selected ? (kind === 'primary' ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-blue-400 bg-blue-50') : 'border-gray-200 hover:border-teal-300 hover:shadow-md bg-white'}`}
    data-testid={testid}
  >
    {children}
  </button>
);

// ─── helper for selection logic ───
function addPick(selections, item, max = DEFAULT_MAX_SELECTIONS) {
  // Guard against malformed items — never store a blank/undefined id, it
  // breaks selection matching (all cards look identical) and crashes the
  // backend with "Preview None doesn't belong to your path".
  if (!item || item.item_id === undefined || item.item_id === null
      || String(item.item_id).trim() === '' || String(item.item_id).toLowerCase() === 'none') {
    return selections;
  }
  const id = String(item.item_id);
  const exists = selections.find((s) => String(s.item_id) === id);
  if (exists) return selections; // dedupe
  if (selections.length >= max) return selections; // hard cap
  const kind = selections.length === 0 ? 'primary' : 'exploring';
  return [...selections, { ...item, item_id: id, kind }];
}

// ─── PATH 3: job_roles (already-built finder, simplified) ───
function JobRolesFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [data, setData] = useState(null);
  const [audience, setAudience] = useState(null);
  const [branch, setBranch] = useState(null);
  const [interests, setInterests] = useState([]);
  const [coding, setCoding] = useState(null);
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchJobRolesData().then(setData); }, []);
  useEffect(() => {
    if (audience === 'final-year' && branch && interests.length && coding) {
      findJobRoles({ branch, interests, coding }).then(setMatches);
    }
  }, [audience, branch, interests, coding]);

  const onPick = (r) => {
    const item = {
      item_type: 'role',
      item_id: String(r.catalog_number),
      item_name_verbatim: r.role_name,
    };
    onSelectionsChange(addPick(selections, item, max));
  };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  if (!audience) {
    return (
      <div data-testid="job-roles-audience">
        <h3 className="text-lg font-bold mb-3">Where are you right now?</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {[['final-year', 'Final-year student'], ['fresher', 'Fresher (0–2 yrs)'], ['working', 'Working professional']].map(([k, l]) => (
            <Card key={k} onClick={() => setAudience(k)} testid={`audience-${k}`}>
              <div className="font-bold text-gray-900">{l}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div data-testid="job-roles-branch">
        <h3 className="text-lg font-bold mb-3">Pick your branch</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {data.branches.map((b) => (
            <Card key={b} onClick={() => setBranch(b)} testid={`branch-${b.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <div className="font-semibold text-gray-900 text-sm">{b}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (audience === 'final-year') {
    // Guided: ask interests + coding
    if (!coding || interests.length === 0) {
      return (
        <div data-testid="job-roles-interests">
          <h3 className="text-lg font-bold mb-2">What do you enjoy?</h3>
          <p className="text-xs text-gray-500 mb-3">Pick 1–3.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
            {data.interest_options.map((opt) => {
              const sel = interests.includes(opt.key);
              const atCap = !sel && interests.length >= 3;
              return (
                <button key={opt.key} type="button" disabled={atCap}
                  onClick={() => setInterests((arr) => arr.includes(opt.key) ? arr.filter((x) => x !== opt.key) : [...arr, opt.key])}
                  className={`px-3 py-2 rounded-lg border text-xs transition ${sel ? 'border-teal-500 bg-teal-50 text-teal-900 font-bold' : atCap ? 'border-gray-100 text-gray-300' : 'border-gray-200 hover:border-teal-300'}`}
                  data-testid={`interest-${opt.key}`}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-sm font-semibold mb-2">How do you feel about coding?</p>
          <div className="grid grid-cols-3 gap-2">
            {data.coding_options.map((opt) => (
              <button key={opt.key} type="button" onClick={() => setCoding(opt.key)}
                className={`px-3 py-2 rounded-lg border text-xs transition ${coding === opt.key ? 'border-blue-500 bg-blue-50 text-blue-900 font-bold' : 'border-gray-200 hover:border-blue-300'}`}
                data-testid={`coding-${opt.key}`}>{opt.label}</button>
            ))}
          </div>
        </div>
      );
    }
    // Show matches
    return (
      <div data-testid="job-roles-matches">
        <h3 className="text-lg font-bold mb-3">Roles that fit you</h3>
        <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
        <div className="grid sm:grid-cols-2 gap-3">
          {matches.map((m) => {
            const sel = selections.find((s) => s.item_id === String(m.catalog_number));
            return (
              <Card key={m.catalog_number} onClick={() => onPick(m)} selected={!!sel} kind={sel?.kind} testid={`match-${m.catalog_number}`}>
                <div className="font-bold text-gray-900">{m.role_name}</div>
                {m.section && <div className="text-xs text-gray-500 mt-1">{m.section}</div>}
                {sel && <div className="text-xs font-semibold text-teal-700 mt-2">{sel.kind === 'primary' ? '★ Primary' : 'Exploring'}</div>}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Fresher / Working → branch + search
  const filtered = data.roles.filter((r) => {
    if (branch && !(r.branch_tags || []).includes(branch)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.role_name.toLowerCase().includes(q);
  }).slice(0, 40);

  return (
    <div data-testid="job-roles-search">
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a role name…" className="pl-10 h-11" data-testid="role-search" />
      </div>
      <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
        {filtered.map((r) => {
          const sel = selections.find((s) => s.item_id === String(r.catalog_number));
          return (
            <Card key={r.catalog_number} onClick={() => onPick(r)} selected={!!sel} kind={sel?.kind} testid={`role-${r.catalog_number}`}>
              <div className="font-semibold text-gray-900 text-sm">{r.role_name}</div>
              {r.section && <div className="text-xs text-gray-500">{r.section}</div>}
            </Card>
          );
        })}
        {filtered.length === 0 && <p className="text-sm text-gray-500 text-center py-6">No matches for that search.</p>}
      </div>
    </div>
  );
}

// ─── PATH 2: specializations (program → branch → cards) ───
function SpecializationsFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [program, setProgram] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (program) { setLoading(true); fetchSpecBranches(program).then((b) => { setBranches(b); setLoading(false); }); } }, [program]);
  useEffect(() => { if (program && branch) { setLoading(true); fetchSpecData(program, branch).then((s) => { setSpecs(s); setLoading(false); }); } }, [program, branch]);

  if (!program) {
    return (
      <div data-testid="spec-program">
        <h3 className="text-lg font-bold mb-3">Which program?</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {['B.Tech', 'BBA', 'MBA'].map((p) => (
            <Card key={p} onClick={() => setProgram(p)} testid={`program-${p.toLowerCase()}`}>
              <div className="font-bold text-gray-900">{p}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  if (!branch) {
    return (
      <div data-testid="spec-branch">
        <h3 className="text-lg font-bold mb-3">Pick your branch first</h3>
        <p className="text-xs text-gray-500 mb-3">Specializations stay hidden until you pick a branch.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {branches.map((b) => (
            <Card key={b} onClick={() => setBranch(b)} testid={`spec-branch-${b.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <div className="font-semibold text-gray-900 text-sm">{b}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div data-testid="spec-list">
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      <div className="grid sm:grid-cols-2 gap-3">
        {specs.map((s) => {
          const sel = selections.find((x) => x.item_id === s.id);
          return (
            <Card key={s.id} onClick={() => onSelectionsChange(addPick(selections, { item_type: 'specialization', item_id: s.id, item_name_verbatim: s.title }, max))}
                  selected={!!sel} kind={sel?.kind} testid={`spec-${s.id}`}>
              <div className="font-bold text-gray-900">{s.title}</div>
              {s.category && <div className="text-xs text-gray-500 mt-1">{s.category}</div>}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── PATH 4: ai_pathway (collapsed departments → expand → pick) ───
function AIPathwayFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [departments, setDepartments] = useState(null);
  const [openDept, setOpenDept] = useState(null);
  useEffect(() => { fetchAIDepartments().then(setDepartments); }, []);

  if (!departments) return <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  return (
    <div data-testid="ai-pathway">
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      <p className="text-xs text-gray-500 mb-3">Tap a department to see its roles.</p>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {departments.map((d) => (
          <div key={d.name} className="border border-gray-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setOpenDept(openDept === d.name ? null : d.name)}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
                    data-testid={`ai-dept-${d.name.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <span className="font-bold text-gray-900">{d.name}</span>
              <span className="text-xs text-gray-500">{openDept === d.name ? 'Hide' : 'Show'}</span>
            </button>
            {openDept === d.name && (
              <div className="p-3 space-y-2 bg-white">
                {d.roles.map((r) => {
                  const sel = selections.find((x) => x.item_id === r.id);
                  return (
                    <Card key={r.id} onClick={() => onSelectionsChange(addPick(selections, { item_type: 'ai_role', item_id: r.id, item_name_verbatim: r.title }, max))}
                          selected={!!sel} kind={sel?.kind} testid={`ai-role-${r.id}`}>
                      <div className="font-semibold text-gray-900 text-sm">{r.title}</div>
                      {r.summary && <div className="text-xs text-gray-500 line-clamp-2 mt-1">{r.summary}</div>}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PATH 5: banking + bluecollar ───
function BankingBlueFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [domain, setDomain] = useState(null);
  const [families, setFamilies] = useState([]);
  const [family, setFamily] = useState(null);
  const [items, setItems] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (domain) { setLoading(true); fetchBankingBlueFamilies(domain).then((r) => { setFamilies(r.families); setLoading(false); }); } }, [domain]);
  useEffect(() => { if (domain && family) { setLoading(true); fetchBankingBlueData(domain, family).then((r) => { setItems(r.items); setNote(r.note || ''); setLoading(false); }); } }, [domain, family]);

  if (!domain) {
    return (
      <div data-testid="banking-domain">
        <h3 className="text-lg font-bold mb-3">Pick your domain</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Card onClick={() => setDomain('banking')} testid="domain-banking">
            <div className="font-bold text-gray-900">Banking & Finance</div>
            <div className="text-xs text-gray-500 mt-1">Bank exams · PO · Clerk · RBI</div>
          </Card>
          <Card onClick={() => setDomain('skilled_service')} testid="domain-blue">
            <div className="font-bold text-gray-900">Skilled-Service Careers</div>
            <div className="text-xs text-gray-500 mt-1">Electrician · Plumber · Beautician · Driver</div>
          </Card>
        </div>
      </div>
    );
  }
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  if (!family) {
    return (
      <div data-testid="banking-family">
        <h3 className="text-lg font-bold mb-3">Pick your area</h3>
        <div className="grid grid-cols-2 gap-2">
          {families.map((f) => (
            <Card key={f} onClick={() => setFamily(f)} testid={`fam-${f.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <div className="font-semibold text-sm text-gray-900">{f}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  const item_type = domain === 'banking' ? 'banking' : 'blue';
  return (
    <div data-testid="banking-items">
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      {note && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-3 flex gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /><div>{note}</div>
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {items.map((it) => {
          const sel = selections.find((x) => x.item_id === it.id);
          return (
            <Card key={it.id} onClick={() => onSelectionsChange(addPick(selections, { item_type, item_id: it.id, item_name_verbatim: it.name }, max))}
                  selected={!!sel} kind={sel?.kind} testid={`bnk-${it.id}`}>
              <div className="font-semibold text-gray-900 text-sm">{it.name}</div>
              {it.summary && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{it.summary}</div>}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── PATH 6: entrepreneurship ───
function EntrepreneurshipFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('ideas');
  useEffect(() => { fetchEntrepreneurshipData().then(setData); }, []);
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  const list = tab === 'ideas' ? data.ideas : data.schemes;
  const item_type = tab === 'ideas' ? 'idea' : 'scheme';
  return (
    <div className="bg-gray-900 -m-6 md:-m-8 p-6 md:p-8 rounded-2xl text-white" data-testid="entrepreneurship">
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      {data.meta?.disclaimer && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 text-xs text-amber-200 mb-3">{data.meta.disclaimer}</div>
      )}
      <div className="flex gap-2 mb-3">
        <button type="button" onClick={() => setTab('ideas')} className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === 'ideas' ? 'bg-rose-500 text-white' : 'bg-gray-700 text-gray-300'}`} data-testid="ent-tab-ideas">Business Ideas</button>
        <button type="button" onClick={() => setTab('schemes')} className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === 'schemes' ? 'bg-rose-500 text-white' : 'bg-gray-700 text-gray-300'}`} data-testid="ent-tab-schemes">Govt Schemes</button>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {list.map((it) => {
          const sel = selections.find((x) => x.item_id === it.id);
          return (
            <button key={it.id} type="button" onClick={() => onSelectionsChange(addPick(selections, { item_type, item_id: it.id, item_name_verbatim: it.name }, max))}
                    className={`w-full text-left p-3 rounded-xl border-2 transition ${sel ? 'border-rose-400 bg-rose-500/10' : 'border-gray-700 hover:border-rose-400/50 bg-gray-800'}`}
                    data-testid={`ent-${item_type}-${it.id}`}>
              <div className="font-bold text-white text-sm">{it.name}</div>
              {it.summary && <div className="text-xs text-gray-400 mt-1 line-clamp-2">{it.summary}</div>}
              {it.warning && <div className="text-[11px] text-amber-300 mt-1">⚠ {it.warning}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── PATH 1: after-10th-inter (clarity quiz teaser) ───
function After10thFlow({ onSelectionsChange, selections, max = DEFAULT_MAX_SELECTIONS }) {
  const [track, setTrack] = useState(null);
  const [confirm, setConfirm] = useState(false);
  // For signup we just record the chosen TRACK as the "primary selection" so the
  // user can take the full quiz inside the dashboard (where attempt-limits live).
  return (
    <div data-testid="after10-track">
      <h3 className="text-lg font-bold mb-3">Pick your track</h3>
      <p className="text-xs text-gray-500 mb-3">After signup you'll get the 4-test Career Clarity quiz (3 attempts max — taken inside the app).</p>
      <SelectionBar selections={selections} onRemove={(s) => onSelectionsChange(selections.filter((x) => x.item_id !== s.item_id))} max={max} />
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          { id: 'btech', label: 'B.Tech Career Clarity', sub: 'Engineering path' },
          { id: 'bba_mba', label: 'BBA / MBA Career Clarity', sub: 'Business / management path' },
        ].map((t) => {
          const sel = selections.find((s) => s.item_id === t.id);
          return (
            <Card key={t.id} onClick={() => { setTrack(t.id); onSelectionsChange(addPick(selections, { item_type: 'clarity_profile', item_id: t.id, item_name_verbatim: t.label }, max)); }}
                  selected={!!sel} kind={sel?.kind} testid={`after10-${t.id}`}>
              <div className="font-bold text-gray-900">{t.label}</div>
              <div className="text-xs text-gray-500 mt-1">{t.sub}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── Dispatcher ───
export default function PathSelectionFlow({ pathKey, selections, onSelectionsChange, maxSelections = DEFAULT_MAX_SELECTIONS }) {
  const Component = {
    after_10th_inter:   After10thFlow,
    specializations:    SpecializationsFlow,
    job_roles:          JobRolesFlow,
    ai_pathway:         AIPathwayFlow,
    banking_bluecollar: BankingBlueFlow,
    entrepreneurship:   EntrepreneurshipFlow,
  }[pathKey];
  if (!Component) return <div className="text-center py-10 text-gray-500">Unknown path.</div>;
  return <Component selections={selections} onSelectionsChange={onSelectionsChange} max={maxSelections} />;
}
